import React, { useContext, useState, useCallback, useEffect } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import cross from '../../../../assets/addStockModal/cross.svg';
import { ActionsDiv, AddOPS, CancelButton, CloseDiv, CloseImg, Container, HeaderDiv, HeaderText, InfoText, MachineDiv, OPDiv, Select, SelectedOPContainer, SelectedOPTag, RemoveTagButton } from './style';
import FashionApiService from '../../../../services/FashionApiService';
import axios from '../../../../config/axios';
import { useNavigate } from 'react-router-dom';

interface Machine {
    id: number;
    code: string;
    name: string;
}

interface FashionProductionOrder {
    id: number;
    set?: string;
    status?: string;
    fashionProductId?: number;
    colorId?: number;
}

const AddFashionOP = () => {
    const { setModalActive } = useContext(ModalContext);

    const [machines, setMachines] = useState<Machine[]>([]);
    const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
    const [isLoadingMachines, setIsLoadingMachines] = useState(true);
    const [errorMachines, setErrorMachines] = useState<string | null>(null);

    const [fashionProductionOrders, setFashionProductionOrders] = useState<FashionProductionOrder[]>([]);
    const [selectedFashionOrderId, setSelectedFashionOrderId] = useState<number | null>(null);
    const [isLoadingFashionOrders, setIsLoadingFashionOrders] = useState(true);
    const [errorFashionOrders, setErrorFashionOrders] = useState<string | null>(null);

    const [selectedOPs, setSelectedOPs] = useState<FashionProductionOrder[]>([]);
    const navigate = useNavigate()

    const fetchMachines = useCallback(async () => {
        setIsLoadingMachines(true);
        setErrorMachines(null);
        try {
            const response = await axios.get('/equipment/equipment');
            setMachines(response.data);
        } catch (err: any) {
            console.error('Erro ao buscar máquinas:', err);
            setErrorMachines('Não foi possível carregar as máquinas.');
        } finally {
            setIsLoadingMachines(false);
        }
    }, []);

    const fetchFashionOrders = useCallback(async () => {
        setIsLoadingFashionOrders(true);
        setErrorFashionOrders(null);
        try {
            const response = await FashionApiService.getProductionOrders();
            setFashionProductionOrders(response.data);
        } catch (err: any) {
            console.error('Erro ao buscar ordens de pedido:', err);
            setErrorFashionOrders('Não foi possível carregar as OPs.');
        } finally {
            setIsLoadingFashionOrders(false);
        }
    }, []);

    useEffect(() => {
        fetchMachines();
        fetchFashionOrders();
    }, [fetchMachines, fetchFashionOrders]);

    const handleMachineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setSelectedMachineId(value === '' ? null : Number(value));
    };

    const handleFashionOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const newSelectedId = value === '' ? null : Number(value);
        setSelectedFashionOrderId(newSelectedId);

        if (newSelectedId !== null) {
            const opToAdd = fashionProductionOrders.find(op => op.id === newSelectedId);
            if (opToAdd && !selectedOPs.some(op => op.id === opToAdd.id)) {
                setSelectedOPs(prevOPs => [...prevOPs, opToAdd]);
            }
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleRemoveOP = (opIdToRemove: number) => {
        setSelectedOPs(prevOPs => prevOPs.filter(op => op.id !== opIdToRemove));
    };

    const handleAddOPs = async () => {
        if (!selectedMachineId || selectedOPs.length === 0) return;

        setIsSubmitting(true);
        try {
            await Promise.all(
                selectedOPs.map(op =>
                    FashionApiService.updateProductionOrder(op.id, { machineId: selectedMachineId })
                )
            );
            navigate(-1);
        } catch (err: any) {
            alert('Erro ao adicionar OPs ao PCP.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isAddButtonDisabled = isLoadingMachines || isLoadingFashionOrders ||
        selectedMachineId === null || selectedOPs.length === 0 || isSubmitting;

    return (
        <Container>
            <HeaderDiv>
                <HeaderText>Adicionar OP</HeaderText>
                <CloseDiv onClick={() => navigate(-1)}>
                    <CloseImg src={cross} alt='Fechar aba' />
                </CloseDiv>
            </HeaderDiv>

            <MachineDiv>
                <InfoText>Máquina</InfoText>
                <Select
                    name="machineSelect"
                    id="machine-select"
                    value={selectedMachineId !== null ? selectedMachineId : ''}
                    onChange={handleMachineChange}
                    disabled={isLoadingMachines || errorMachines !== null}
                >
                    <option value="" disabled={selectedMachineId !== null}>
                        {isLoadingMachines ? 'Carregando máquinas...' :
                            errorMachines ? `Erro: ${errorMachines}` :
                                machines.length === 0 ? 'Nenhuma máquina encontrada' :
                                    'Selecione a Máquina'}
                    </option>
                    {!isLoadingMachines && !errorMachines && machines.length > 0 && (
                        machines.map(machine => (
                            <option key={machine.id} value={machine.id}>
                                {machine.code} - {machine.name}
                            </option>
                        ))
                    )}
                </Select>
            </MachineDiv>

            <OPDiv>
                <InfoText>Selecionar OP</InfoText>
                <Select
                    name="opSelect"
                    id="op-select"
                    value={selectedFashionOrderId !== null ? selectedFashionOrderId : ''}
                    onChange={handleFashionOrderChange}
                    disabled={isLoadingFashionOrders || errorFashionOrders !== null}
                >
                    <option value="" disabled={selectedFashionOrderId !== null}>
                        {isLoadingFashionOrders ? 'Carregando OPs...' :
                            errorFashionOrders ? `Erro: ${errorFashionOrders}` :
                                fashionProductionOrders.length === 0 ? 'Nenhuma OP encontrada' :
                                    'Selecione a OP'}
                    </option>
                    {!isLoadingFashionOrders && !errorFashionOrders && fashionProductionOrders.length > 0 && (
                        fashionProductionOrders
                            .filter(order => !selectedOPs.some(selected => selected.id === order.id))
                            .map(order => (
                                <option key={order.id} value={order.id}>
                                    OP - {order.id} {order.set ? ` - ${order.set}` : ''}
                                </option>
                            ))
                    )}
                </Select>

                {selectedOPs.length > 0 && (
                    <SelectedOPContainer>
                        {selectedOPs.map(op => (
                            <SelectedOPTag key={op.id}>
                                {op.set || `OP - ${op.id}`}
                                <RemoveTagButton onClick={() => handleRemoveOP(op.id)}>X</RemoveTagButton>
                            </SelectedOPTag>
                        ))}
                    </SelectedOPContainer>
                )}
            </OPDiv>

            <ActionsDiv>
                <CancelButton onClick={() => navigate(-1)}>Cancelar</CancelButton>
                <AddOPS
                    onClick={handleAddOPs}
                    disabled={isAddButtonDisabled}
                >
                    + Adicionar OPs
                </AddOPS>
            </ActionsDiv>
        </Container>
    );
};

export default AddFashionOP;